// API service for chat functionality
import { ChatListResponse, ChatDetailResponse, ChatSearchResponse } from '../types';
import { apiFetch } from '../../../services/api';

const API_BASE_URL = (process.env.REACT_APP_CHAT_API_BASE_URL ?? '').replace(/\/+$/, '');

export interface MessageData {
  role: string;
  content: string;
  sources: any[];
}

export interface UsageInfo {
  limit: number;
  used: number;
  remaining: number;
  resets_at?: string | null;
}

export interface ChatResponse {
  chat_id: string | null;
  message: {
    message_id: string;
    question: string;
    original_language: string;
    answer: string;
    question_timestamp: string;
    answer_timestamp: string;
    is_related: boolean;
    enable_web_search: boolean;
    references: any;
    related_sdgs: string;
    reask?: string[];
  };
  usage?: UsageInfo;
}

export interface UsageStatsResponse {
  success: boolean;
  data: {
    daily_limit: number;
    used_today: number;
    remaining_today: number;
    is_unlimited: boolean;
    resets_at?: string | null;
  };
}

// Custom error class for rate limit exceeded
export class RateLimitError extends Error {
  usage: UsageInfo;

  constructor(message: string, usage: UsageInfo) {
    super(message);
    this.name = 'RateLimitError';
    this.usage = usage;
  }
}

interface SendChatMessageOptions {
  signal?: AbortSignal;
  useRag?: boolean;
}

export const sendChatMessage = async (
  message: string,
  chatId: string | null = null,
  options?: SendChatMessageOptions
): Promise<ChatResponse> => {
  const { signal, useRag = true } = options || {};
  try {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/chats/`, {
      method: 'POST',
      body: JSON.stringify({
        chat_id: chatId,
        message: message,
        use_rag: useRag
      }),
      signal,
    }, true);

    // Handle rate limit exceeded (429)
    if (response.status === 429) {
      const errorData = await response.json();
      throw new RateLimitError(
        errorData.message || 'Daily limit exceeded',
        {
          limit: errorData.usage?.limit || 30,
          used: errorData.usage?.used || 30,
          remaining: errorData.usage?.remaining || 0,
          resets_at: errorData.usage?.resets_at || null
        }
      );
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    if (error instanceof RateLimitError) {
      throw error;
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Response stopped by user');
    }
    throw error;
  }
};

export const getChatList = async (
  limit: number = 20,
  offset: number = 0
): Promise<ChatListResponse> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/list/?limit=${limit}&offset=${offset}`,
      { method: 'GET' },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching chat list:', error);
    throw error;
  }
};

export const getChatDetail = async (
  chatId: string
): Promise<ChatDetailResponse> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/${chatId}/`,
      { method: 'GET' },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching chat detail:', error);
    throw error;
  }
};

export const updateChatTitle = async (
  chatId: string,
  title: string
): Promise<{ chat_id: string; title: string; updated_at: string }> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/${chatId}/update/`,
      {
        method: 'PATCH',
        body: JSON.stringify({ title }),
      },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error updating chat title:', error);
    throw error;
  }
};

export const resendChatMessage = async (
  messageId: string,
  message?: string,
  useRag: boolean = true,
  signal?: AbortSignal
): Promise<ChatResponse> => {
  try {
    const response = await apiFetch(`${API_BASE_URL}/api/v1/chats/resent/`, {
      method: 'POST',
      body: JSON.stringify({
        message_id: messageId,
        ...(message ? { message } : {}),
        use_rag: useRag
      }),
      signal,
    }, true);

    // Handle rate limit exceeded (429)
    if (response.status === 429) {
      const errorData = await response.json();
      throw new RateLimitError(
        errorData.message || 'Daily limit exceeded',
        {
          limit: errorData.usage?.limit || 30,
          used: errorData.usage?.used || 30,
          remaining: errorData.usage?.remaining || 0,
          resets_at: errorData.usage?.resets_at || null
        }
      );
    }

    if (response.ok || response.status === 404) {
      return await response.json();
    }

    const errorData = await response.json().catch(() => null);
    const errorMessage =
      (errorData && ('error' in errorData ? errorData.error : errorData.message)) ||
      `HTTP error! status: ${response.status}`;
    throw new Error(errorMessage);
  } catch (error) {
    if (error instanceof RateLimitError) {
      throw error;
    }
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Response stopped by user');
    }
    throw error;
  }
};

export const deleteChat = async (
  chatId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/${chatId}/delete/`,
      { method: 'DELETE' },
      true
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const errorMessage =
        (data && ('error' in data ? data.error : data.message)) ||
        `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return data ?? { success: true };
  } catch (error) {
    console.error('Error deleting chat:', error);
    throw error;
  }
};

export const searchChatMessages = async (
  keyword: string,
  limit: number = 50
): Promise<ChatSearchResponse> => {
  if (!keyword) {
    throw new Error('keyword parameter is required');
  }

  const params = new URLSearchParams({
    keyword,
    ...(limit ? { limit: String(Math.min(Math.max(limit, 1), 200)) } : {}),
  });

  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/messages/search/?${params.toString()}`,
      { method: 'GET' },
      true
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage =
        (errorData && ('error' in errorData ? errorData.error : errorData.message)) ||
        `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Error searching chat messages:', error);
    throw error;
  }
};

export const getUsageStats = async (): Promise<UsageStatsResponse> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/usage/`,
      { method: 'GET' },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching usage stats:', error);
    throw error;
  }
};

export const submitMessageFeedback = async (
  messageId: string,
  feedback: 'like' | 'dislike' | null
): Promise<{ success: boolean; message_id: string; user_feedback: 'like' | 'dislike' | null }> => {
  try {
    const response = await apiFetch(
      `${API_BASE_URL}/api/v1/chats/messages/${messageId}/feedback/`,
      {
        method: 'POST',
        body: JSON.stringify({ feedback }),
      },
      true
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error submitting message feedback:', error);
    throw error;
  }
};